import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  ProfileSections,
  profileSectionsToString,
} from "@skills-app/models/ProfileSections";
import * as d3 from "d3";

const permittedDisplayStyle = [
  "round",
  "round-all",
  "horizontal",
  "horizontal-all",
] as const;

type DisplayStyle = (typeof permittedDisplayStyle)[number];

@Component({
  standalone: false,
  selector: "app-profile-completeness-indicator",
  templateUrl: "./profile-completeness-indicator.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./profile-completeness-indicator.component.css"],
})
export class ProfileCompletenessIndicatorComponent
  implements OnInit, OnChanges
{
  @Input() displayProfileSections: ProfileSections =
    0 as unknown as ProfileSections;
  @Input() isComplete = false;
  @Input() displayStyle: DisplayStyle | string = "horizontal";
  @Input() isConfidentialityFeatureEnabled = true;

  pills: Array<{
    section: ProfileSections;
    exists: boolean;
    path?: string | null;
  }> = [];

  pillWidth = 40;
  pillGap = 10;
  tooltipMessage = "";
  viewBox = "";
  pillPaths: Array<string | null> = [];

  ngOnInit(): void {
    this.normalizeDisplayStyle();
    this.generatePills();
    this.generateTooltip();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["displayProfileSections"] ||
      changes["displayStyle"] ||
      changes["isConfidentialityFeatureEnabled"]
    ) {
      this.normalizeDisplayStyle();
      this.generatePills();
      this.generateTooltip();
    }
  }

  private normalizeDisplayStyle(): void {
    if (!permittedDisplayStyle.includes(this.displayStyle as DisplayStyle)) {
      this.displayStyle = "horizontal";
    }
  }

  private generatePills(): void {
    this.pills = [];

    this.pills.push({
      section: ProfileSections.PrimaryCapability,
      exists: this.hasProfileSection(ProfileSections.PrimaryCapability),
    });
    this.pills.push({
      section: ProfileSections.AboutMe,
      exists: this.hasProfileSection(ProfileSections.AboutMe),
    });
    this.pills.push({
      section: ProfileSections.Projects,
      exists: this.hasProfileSection(ProfileSections.Projects),
    });
    this.pills.push({
      section: ProfileSections.Skills,
      exists: this.hasProfileSection(ProfileSections.Skills),
    });

    if (this.isConfidentialityFeatureEnabled) {
      this.pills.push({
        section: ProfileSections.Confidentiality,
        exists: this.hasProfileSection(ProfileSections.Confidentiality),
      });
    }

    this.pills.push({
      section: ProfileSections.Industries,
      exists: this.hasProfileSection(ProfileSections.Industries),
    });

    if (
      this.hasProfileSection(ProfileSections.Education) ||
      this.displayStyle === "horizontal-all" ||
      this.displayStyle === "round-all"
    ) {
      this.pills.push({
        section: ProfileSections.Education,
        exists: this.hasProfileSection(ProfileSections.Education),
      });
    }

    if (
      this.hasProfileSection(ProfileSections.Certifications) ||
      this.displayStyle === "horizontal-all" ||
      this.displayStyle === "round-all"
    ) {
      this.pills.push({
        section: ProfileSections.Certifications,
        exists: this.hasProfileSection(ProfileSections.Certifications),
      });
    }

    if (
      this.displayStyle === "horizontal" ||
      this.displayStyle === "horizontal-all"
    ) {
      const maxExpectedPills = 6;
      const pillCount =
        this.pills.length > maxExpectedPills
          ? this.pills.length
          : maxExpectedPills;

      this.viewBox = `-5 -5 ${this.pillGap + (this.pillWidth + this.pillGap) * pillCount} ${
        this.pillWidth / 2 + 10
      }`;
    } else {
      this.pillPaths = this.generateCircularPaths();
      this.viewBox = "-37 -37 74 74";

      for (let index = 0; index < this.pills.length; index++) {
        this.pills[index].path = this.pillPaths[index];
      }
    }
  }

  private generateTooltip(): void {
    this.tooltipMessage = this.pills
      .map(
        (pill) =>
          `${profileSectionsToString(pill.section)} : ${
            pill.exists ? "Complete" : "Incomplete"
          }`,
      )
      .join("\n");
  }

  private hasProfileSection(section: ProfileSections): boolean {
    return (this.displayProfileSections & section) === section;
  }

  private generateCircularPaths(): Array<string | null> {
    const totalAnglePerSliceRad = (Math.PI * 2) / this.pills.length;
    const angleGapRad = totalAnglePerSliceRad * (1.0 / 8.0);

    const innerRadius = 15;
    const outerRadius = 35;

    const arc = d3
      .arc<d3.DefaultArcObject>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .padAngle(angleGapRad);

    const arcPaths: Array<string | null> = [];

    for (let index = 0; index < this.pills.length; index++) {
      const datum: d3.DefaultArcObject = {
        startAngle: index * totalAnglePerSliceRad,
        endAngle: (index + 1) * totalAnglePerSliceRad,
        padAngle: angleGapRad,
        innerRadius,
        outerRadius,
      };

      arcPaths.push(arc(datum));
    }

    return arcPaths;
  }
}
